import React from 'react';

// Support both snake_case (API) and camelCase formats
interface PIPItem {
  pipId?: string;
  pipTitle?: string;
  overallStatus?: string;
  periodStart?: string;
  periodEnd?: string;
  managerName?: string;
  totalMilestones?: number;
  completedMilestones?: number;
  // snake_case (from API)
  pip_id?: string;
  pip_title?: string;
  overall_status?: string;
  period_start?: string;
  period_end?: string;
  manager_name?: string;
  total_milestones?: number;
  completed_milestones?: number;
}

// Helpers for snake_case/camelCase compatibility
const getPipId = (p: PIPItem) => p.pipId || p.pip_id || '';
const getPipTitle = (p: PIPItem) => p.pipTitle || p.pip_title || 'Performance Improvement Plan';
const getOverallStatus = (p: PIPItem) => p.overallStatus || p.overall_status || 'Pending';
const getPeriodStart = (p: PIPItem) => p.periodStart || p.period_start;
const getPeriodEnd = (p: PIPItem) => p.periodEnd || p.period_end;
const getManagerName = (p: PIPItem) => p.managerName || p.manager_name || '';
const getTotalMilestones = (p: PIPItem) => Number(p.totalMilestones || p.total_milestones || 0);
const getCompletedMilestones = (p: PIPItem) => Number(p.completedMilestones || p.completed_milestones || 0);

interface PIPsWidgetProps {
  pips?: PIPItem[] | { data: PIPItem[] };
  loading?: boolean;
  error?: string;
}

const formatDate = (dateStr?: string): string => {
  if (!dateStr) return '';
  const date = new Date(dateStr);
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
};

const getStatusBadge = (status: string): { className: string } => {
  const s = status?.toLowerCase().replace(/_/g, ' ');
  if (s === 'in progress') return { className: 'bg-amber-200 text-amber-800' };
  if (s === 'pending') return { className: 'bg-blue-100 text-blue-700' };
  if (s === 'waiting for confirmation') return { className: 'bg-purple-100 text-purple-700' };
  if (s === 'closed') return { className: 'bg-emerald-100 text-emerald-700' };
  return { className: 'bg-gray-100 text-gray-600' };
};

export const PIPsWidget: React.FC<PIPsWidgetProps> = ({ pips, loading, error }) => {
  // Handle case where data might be wrapped in { data: [...] }
  const pipsList: PIPItem[] = Array.isArray(pips) ? pips : ((pips as { data: PIPItem[] })?.data || []);
  const pipCount = pipsList.length;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full py-8">
        <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-amber-600" />
      </div>
    );
  }

  if (error) {
    return <div className="flex items-center justify-center h-full py-8 text-red-500 text-sm">{error}</div>;
  }

  return (
    <div className="p-5 h-full flex flex-col">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <h3 className="font-semibold text-gray-900 text-sm">PIPs</h3>
        <span className="min-w-[20px] h-5 bg-amber-100 text-amber-700 rounded-full flex items-center justify-center text-[11px] font-semibold px-1.5">
          {pipCount}
        </span>
      </div>

      {/* PIP Cards */}
      <div className="flex-1 min-h-0 space-y-3 overflow-y-auto">
        {pipsList.length === 0 ? (
          <div className="text-center py-6 text-gray-500 text-sm">No active PIPs</div>
        ) : (
          pipsList.map((pip) => {
            const total = getTotalMilestones(pip);
            const completed = getCompletedMilestones(pip);
            const progressPercent = total > 0 ? (completed / total) * 100 : 0;
            const status = getOverallStatus(pip);
            const badge = getStatusBadge(status);

            return (
              <div key={getPipId(pip)} className="p-4 bg-amber-50 border border-amber-200 rounded-lg">
                {/* Title & Status */}
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium text-gray-900">{getPipTitle(pip)}</span>
                  <span className={`text-[11px] font-medium px-2 py-0.5 rounded-full ${badge.className}`}>
                    {status}
                  </span>
                </div>

                {/* Dates */}
                <div className="text-xs text-gray-500 mb-3">
                  Started: {formatDate(getPeriodStart(pip))} · Review: {formatDate(getPeriodEnd(pip))}
                </div>

                {/* Milestone Progress */}
                {total > 0 && (
                  <>
                    <div className="flex items-center justify-between mb-1.5">
                      <span className="text-xs text-gray-600">Milestone Progress</span>
                      <span className="text-xs font-medium text-amber-700">{completed} of {total}</span>
                    </div>
                    <div className="w-full h-2 bg-amber-100 rounded-full overflow-hidden">
                      <div
                        className="h-full bg-amber-500 rounded-full transition-all duration-300"
                        style={{ width: `${progressPercent}%` }}
                      />
                    </div>
                  </>
                )}

                {/* Manager */}
                {getManagerName(pip) && (
                  <div className="mt-3 flex items-center gap-2 text-xs text-gray-500">
                    <svg className="w-3.5 h-3.5 flex-shrink-0" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                      <path d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"/>
                    </svg>
                    <span>Manager: {getManagerName(pip)}</span>
                  </div>
                )}
              </div>
            );
          })
        )}
      </div>
    </div>
  );
};

export default PIPsWidget;
